/**
 * src/hooks/useVoiceConversation.js — Voice Conversation Orchestrator.
 * Composes state machine + TTS + STT + auto-VAD + barge-in + audio-
 * session cleanup. Keeps voice.chat SSE glue + 76-02 proposal wiring.
 * Public API: state, recording, transcribing, isSpeaking, transcript,
 * lastAiText, lastAiFullText, lastToolCalls, error, packetUsed,
 * voiceMode, pendingProposal, confirmProposal, cancelProposal,
 * startTurn, stopRecording, stopSpeaking, endSession, unlockAudio.
 */

import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { supabase } from '../lib/supabase';
import AuthContext from '../auth/AuthContext';
import { getAuthHeaders } from '../lib/api/client';
import { parseSSE } from '../lib/api/sse';
import { useAgentMode } from '../lib/agent/agentModeContext.jsx';
import useWhisperRecording from './useWhisperRecording';
import useStreamingTTS from './useStreamingTTS';
import { createSentenceBuffer } from '../lib/voice/sentenceBuffer';
import { logVoiceEvent, startTimer } from '../lib/voice/voiceLogger';
import { useAppDataContext } from '../lib/AppDataContext';
import { useVoiceMode } from '../lib/voice/voiceMode';
import useVoiceStateMachine from '../lib/voice/useVoiceStateMachine';
import useVoiceAutoMode from '../lib/voice/useVoiceAutoMode';
import useVoiceBargeIn from '../lib/voice/useVoiceBargeIn';
import useAudioSessionCleanup from '../lib/voice/useAudioSessionCleanup';
import { handleVoiceSseEvent } from '../lib/voice/voiceSseHandler';
import { handleCopilotVoiceEvent } from '../lib/voice/copilotVoiceEvent';
import { buildVoicePacket } from '../lib/voice/voicePacketBuilder';
import { createTurnLatencyTracker } from '../lib/voice/turnLatency';
import { ensureVoiceSession, ensureChatConversationForVoice, updateVoiceSessionTurn, closeVoiceSession } from '../lib/voice/voiceSession';
import { appendMessage } from '../lib/api/agent';
import { persistVoiceProposal, resolveVoiceProposal } from '../lib/voice/voiceProposal';
import { detectSttHallucination } from '../lib/voice/sttHallucinationFilter';
import { normalizeTranscriptSync, buildCorrectionMap, insertCorrectionIntoMap } from '../lib/voice/vocabularyService';
import { buildNameIndex, correctProperNounsSync } from '../lib/voice/properNounCorrect';
import { getStreamingSttModel } from '../lib/voice/voiceProcessFn';

const API_AI = '/api/ai';

// Bug 1 (2026-06-07) — voice turns are persisted to chat_messages via
// appendMessage(source='voice'), but the already-mounted main chat (useChat)
// only re-reads rows on a conversationId change. Notify it to refetch the
// SAME conversation after each voice turn lands. Wrapped so a dispatch failure
// can never break the voice flow (synchronous window event, no async).
function notifyChatMessagesAppended(conversationId) {
  if (!conversationId || typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('chat-messages-appended', {
      detail: { conversationId },
    }));
  } catch (err) {
    logVoiceEvent('chat_messages_appended_dispatch', 'error',
      err?.message || 'dispatch_failed', { conversation_id: conversationId });
  }
}

export default function useVoiceConversation({
  contextProjectId, contextDealId, conversationHistory, activeView,
  // 2026-04-20 — callers can pin the voice mode regardless of the user's
  // localStorage preference. VoiceModeOverlay passes 'auto' because the
  // overlay IS the continuous-conversation surface; on iOS the overlay
  // opened with the default 'ptt' mode (non-'/voice' route), which
  // disables auto-VAD and leaves the orb idle with nothing to press.
  forceVoiceMode,
  // 2026-04-20 Phase 2 (audit P1-5): when the overlay is opened from an
  // existing chat conversation, reuse that conversation id instead of
  // unconditionally creating a new "Voice session" conversation. Voice +
  // text turns share one timeline. Null = create a new chat row as before.
  existingChatConversationId = null,
  // 2026-04-21 persistence fix — callback for parent (AgentShell) so when
  // voice creates a NEW conversation (existingChatConversationId was null),
  // the parent can setCurrentId(cid) and its chat view subscribes to the
  // same conversation. Without this, closing the overlay wipes the turns
  // from the CEO's perspective (they went to an orphan "Voice session" row).
  onConversationPersisted,
  // 2026-04-21 (C1/W4 root-cause fix) — language hint for the STT pipeline.
  // Previously useWhisperRecording hardcoded 'auto' → server mapped to
  // undefined → Whisper auto-detected on silence → emitted Russian/Ukrainian
  // YouTube boilerplate. Callers (useVoiceModePresentation) thread the user's
  // picker selection (voiceOptions.getStoredLanguage). Defaults to 'en'.
  language = 'en',
  // TICKET-912 slice 3 (CEO: reuse the chatbot's voice): { dealId, quoteId, onTurnDone }.
  // When set, turns go to the deal's Quote Copilot (agent.chat scope quote_copilot,
  // stream) instead of voice.chat. The copilot persists its own thread, so no
  // chat_messages writes; its answer arrives whole in `done` and is spoken from there.
  copilot = null,
} = {}) {
  // Ref-captured callback so the inner then() always calls the latest
  // instance without re-memoizing handleTranscript on every parent render.
  const onConversationPersistedRef = useRef(onConversationPersisted);
  onConversationPersistedRef.current = onConversationPersisted;
  const copilotRef = useRef(copilot);
  copilotRef.current = copilot;

  // 2026-06-07 — vocab-on-final. The streaming-STT path (VITE_STREAMING_STT)
  // delivers a RAW transcript; the batch path is corrected server-side in
  // lib/utils/stt.js (applyCorrections), which streaming bypasses. Prefetch the
  // domain_vocabulary alias map ONCE (RLS allows authenticated SELECT) and apply
  // it in handleTranscript so the DISPLAYED transcript + the agent input both get
  // the canonical vessel/project term (e.g. "muruk" -> "Muluc"). Idempotent for
  // the batch path (already-correct text -> no-op). CARL development-010:
  // vessel-name grounding is the differentiator. CEO report 2026-06-07.
  const correctionMapRef = useRef(new Map());
  // TICKET-095 — extracted into a reusable callback so the proposal-confirm
  // path can refetch MID-SESSION. The map used to load once on mount, which
  // is why a confirmed correction only took effect after a reload.
  const refreshCorrectionMap = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('domain_vocabulary')
        .select('term, misheard_as, category, id');
      if (error || !Array.isArray(data)) {
        if (error) {
          // AEGIS P3-02: emit to app_error_logs so CEO can correlate "Muluc not
          // recognized" complaints with a mount-time prefetch failure trace.
          logVoiceEvent('vocab_prefetch', 'error', error.message);
        }
        return;
      }
      // Assigning a ref post-unmount is harmless, so no cancelled guard needed.
      correctionMapRef.current = buildCorrectionMap(data);
    } catch (err) {
      // AEGIS P3-02: non-blocking but now observable — CEO can see prefetch misses
      // in app_error_logs when vessel/project name correction stops working.
      logVoiceEvent('vocab_prefetch', 'error', err?.message ?? 'unknown');
    }
  }, []);
  useEffect(() => { refreshCorrectionMap(); }, [refreshCorrectionMap]);

  const [transcript, setTranscript] = useState('');
  const [lastAiText, setLastAiText] = useState('');
  const [lastAiFullText, setLastAiFullText] = useState('');
  const [lastToolCalls, setLastToolCalls] = useState([]);
  const [error, setError] = useState(null);
  const [packetUsed, setPacketUsed] = useState(null);
  // Phase 91 / T2.1+T2.2 — chief-of-staff queue: the LLM may emit multiple
  // tool_proposal events in a single turn ("create task A, then bundle it
  // into project X"). Older code expected a single slot; we now hold an
  // ordered array of proposals and expose `pendingProposal` (head) as a
  // backwards-compat alias for any consumer that still reads the singular.
  const [pendingProposals, setPendingProposals] = useState([]);
  const pendingProposal = pendingProposals[0] || null;
  // Mirror the queue in a ref so sendToAgent's post-stream "no proposals →
  // idle" check sees the LATEST count rather than the value captured when
  // sendToAgent was last memoized. SSE events fire enqueueProposal mid-stream,
  // so the React state update lands AFTER the stream loop reads its closure.
  const pendingProposalsRef = useRef([]);
  // TICKET-463 — true from the moment speak() is called for this turn.
  // isSpeaking/isPending are React state and are STALE at the end of the SSE
  // loop, so the turn-completion branch below used to fire while TTS was still
  // in flight: it sent the UI to idle ~1.3 s before audio actually started, and
  // it orphaned the turn-latency tracker (0 rows in production against 241
  // recorded turns). A ref is immune to that flush race, which is exactly why
  // pendingProposalsRef already exists. Reset at turn start.
  const ttsRequestedRef = useRef(false);
  const enqueueProposal = useCallback((p) => {
    // Update the ref synchronously so sendToAgent's post-stream check (which
    // runs in the same microtask as the SSE event) sees the latest count.
    // React 18 batches state updaters and runs them on a later tick, so the
    // setState callback alone is too late.
    if (p?.proposal_id && pendingProposalsRef.current.some((x) => x.proposal_id === p.proposal_id)) return;
    const next = [...pendingProposalsRef.current, p];
    const bounded = next.length > 8 ? next.slice(-8) : next;
    pendingProposalsRef.current = bounded;
    setPendingProposals(bounded);
    // T2.3 — persist for audit. Fire-and-forget; voice UX must not block.
    persistVoiceProposal({ supabase, proposal: p, sessionId: sessionIdRef.current });
  }, []);
  const dequeueProposal = useCallback((proposalId) => {
    const next = pendingProposalsRef.current.filter((p) => p.proposal_id !== proposalId);
    pendingProposalsRef.current = next;
    setPendingProposals(next);
  }, []);
  const clearProposals = useCallback(() => {
    pendingProposalsRef.current = [];
    setPendingProposals([]);
  }, []);

  const app = useAppDataContext();
  const [storedVoiceMode] = useVoiceMode();
  const voiceMode = forceVoiceMode || storedVoiceMode;

  // 2026-06-06 (Conversation Telemetry Baseline) — real auth uid for CARL
  // user_id on every voice emit. Read from the auth session (NOT invented).
  // Use useContext(AuthContext) directly rather than useAuth() so a missing
  // provider yields null instead of throwing — telemetry is an observer and
  // must never break the voice path (or the many hook tests that render
  // useVoiceConversation without an <AuthProvider>). Mirrored in a ref so the
  // memoized callbacks always see the latest id without re-memoizing on every
  // auth refresh.
  const authCtx = useContext(AuthContext);
  const userIdRef = useRef(null);
  userIdRef.current = authCtx?.user?.id ?? null;
  // 2026-06-06 — per-turn end-to-end latency tracker. Created in sendToAgent
  // once the turn trace_id is minted; markSpeechEnd at transcript accept,
  // markFirstToken on first SSE text, markFirstAudio via TTS onFirstAudio,
  // flush once after the stream completes. One row per turn keyed by trace_id.
  const turnLatencyRef = useRef(null);
  // Measured server timings for the in-flight turn (agent_ms / stt_ms),
  // stashed at stream-complete and attached when the turn_latency flushes.
  const turnFlushMetaRef = useRef(null);
  // STT segment duration (speech-end -> transcript ready) measured in
  // handleTranscript; stashed so sendToAgent's flush can report stt_ms even
  // though the STT timing happened before the turn trace_id was minted.
  const sttDurationMsRef = useRef(null);

  const sessionIdRef = useRef(null);
  // PAUL Plan 92-01 — per-conversation trace minted at first user turn so the
  // voice.chat round-trip joins the recording's trace via X-Trace-Id header.
  // Recording trace (from useWhisperRecording) is the canonical one for the
  // STT side; this conversation trace covers chat-only turns (typed) and the
  // server can correlate them via session_id when needed.
  const conversationTraceIdRef = useRef(null);
  // B4: chat_conversations row linked to this voice session so each turn is
  // persisted via appendMessage(source='voice'). Created lazily on first turn.
  // Phase 2 (audit P1-5): seed from `existingChatConversationId` so a voice
  // overlay opened from an active chat writes its turns into the SAME
  // conversation instead of spawning a new "Voice session" row.
  const chatConversationIdRef = useRef(existingChatConversationId || null);
  // Phase 2 (audit P1-5): if the overlay stays mounted and the host swaps
  // to a different conversation (CEO clicks another thread in the sidebar
  // while the overlay is open), sync the ref. Only overwrites when the
  // ref is null or the prop changed to a new non-null value — never
  // clobbers a newly-created Voice-session convo mid-turn.
  useEffect(() => {
    if (!existingChatConversationId) return;
    if (chatConversationIdRef.current === existingChatConversationId) return;
    chatConversationIdRef.current = existingChatConversationId;
  }, [existingChatConversationId]);
  const turnTranscriptRef = useRef([]);
  const streamDoneRef = useRef(false);
  const abortControllerRef = useRef(null);
  // Phase 1 (audit P0-3): belt-and-suspenders guard. Even with the cancel
  // token in useWhisperRecording, if a finalize response somehow slips
  // through (race with JS event loop), handleTranscript drops the text
  // instead of dispatching it into a closed voice conversation.
  const sessionClosedRef = useRef(false);
  // Cache of the last user transcript so retryLastTurn can replay without
  // re-recording (Phase 2 of the voice-robustness plan).
  const lastUserTurnRef = useRef(null);
  // 2026-04-20 — Pause button also suspends the idle-VAD. Pre-fix,
  // pause only halted TTS, so speaking while "paused" still triggered
  // VAD → startTurn → "thinking", defeating the pause semantics.
  //
  // 2026-04-20 (session 4) — was a ref. Mutating a ref does NOT re-trigger
  // useVoiceAutoMode's effect (refs don't participate in React's diff), so
  // Pause was effectively a no-op for VAD. Converted to useState so toggling
  // changes `enabled` on the hook → effect cleanup releases the VAD stream.
  const [vadPaused, setVadPaused] = useState(false);
  const sentenceBufRef = useRef(null);
  const aiTextRef = useRef('');
  const aiFullTextRef = useRef('');
  const toolCallsRef = useRef([]);
  const partialsRef = useRef({});
  const stopWhisperRef = useRef(() => {});
  const vadStreamRef = useRef(null);

  // toError is declared below in stateMachine; thread it via ref so
  // useStreamingTTS (declared first) can surface fatal errors through it.
  const toErrorRef = useRef(null);
  const handleTtsError = useCallback((reason, meta) => {
    const msg = reason === 'tts_timeout' ? 'Voice response timed out — tap to retry'
      : reason === 'tts_http_error' ? `Voice service error (${meta?.status || 'unknown'})`
      : reason === 'tts_fetch_error' ? 'Voice service unreachable — check connection'
      : reason === 'audio_not_unlocked' ? 'Tap the mic to enable audio'
      : reason === 'audio_suspended' ? 'Audio was suspended — tap the mic to resume'
      : reason === 'audio_decode_failed' ? 'Could not play voice response'
      : 'Voice playback failed';
    setError(msg);
    // P6 (2026-06-10): pass reason as-is — it already carries the full token
    // (e.g. 'tts_timeout'). The old `tts_${reason}` produced 'tts_tts_timeout',
    // a doubled prefix that caused voice_state_invalid in the FSM.
    toErrorRef.current?.(reason);
  }, []);

  // F3 (2026-04-21) — AgentMode gate on TTS. Even though this hook
  // only runs while VoiceModeOverlay is mounted (F1), the gate is the
  // belt-and-braces layer. If anything ever re-introduces a background
  // hook chain, speak() refuses to enqueue when mode !== 'conversation'.
  // useAgentMode is context-safe outside a provider (returns idle +
  // no-op setters) — tests that render this hook without a provider
  // still work.
  const { mode: _agentMode } = useAgentMode();
  const agentModeRef = useRef(_agentMode);
  agentModeRef.current = _agentMode;
  const isSpeakAllowed = useCallback(() => {
    // During a conversation turn, mode MUST be 'conversation'. Any
    // other mode (idle / capture) means the hook is running in a
    // context where agent audio is not wanted.
    return agentModeRef.current === 'conversation';
  }, []);

  // TICKET-073 (karaoke) — last per-word boundary from the TTS hook
  // ({ seq, wordIndex, globalWordIndex, wordCount } | null). The presentation
  // hook maps it to the highlighted word in the speaking reply bubble.
  const [karaoke, setKaraoke] = useState(null);
  // TICKET-073 (karaoke) — observability: emit exactly ONE spine event when the
  // first word boundary of a speaking streak arrives (null→non-null), so prod
  // can confirm onWordBoundary actually fired on the device (CEO 2026-06-09:
  // "no karaoke effect at all"). Re-armed when the signal clears (barge-in /
  // stop()). NOT per-word — that would spam app_error_logs.
  const karaokeFirstSeenRef = useRef(false);
  const handleWordBoundary = useCallback((boundary) => {
    if (boundary && typeof boundary.globalWordIndex === 'number') {
      if (!karaokeFirstSeenRef.current) {
        karaokeFirstSeenRef.current = true;
        logVoiceEvent('tts_karaoke', 'ok', 'first word boundary', {
          seq: boundary.seq,
          word_index: boundary.wordIndex,
          global_word_index: boundary.globalWordIndex,
          word_count: boundary.wordCount,
        });
      }
    } else {
      // null (stop/barge-in) — re-arm so the next streak logs its first word.
      karaokeFirstSeenRef.current = false;
    }
    setKaraoke(boundary);
  }, []);

  const { speak, stop: stopTTS, pause: pauseTTS, resume: resumeTTS, isSpeaking, isPending, unlockAudio, audioContextRef, isSourceActive, getReferenceEnvelope } = useStreamingTTS({
    onError: handleTtsError,
    isSpeakAllowed,
    // 2026-06-06 — first audible byte of the agent reply. This is the moment
    // the CEO stops waiting; it closes the user-perceived turn_latency window.
    onFirstAudio: () => { try { turnLatencyRef.current?.markFirstAudio(); } catch { /* observer must not break playback */ } },
    // TICKET-073 (karaoke) — per-word highlight pacing for the reply bubble.
    // handleWordBoundary wraps setKaraoke to emit one spine event on the first
    // word boundary of each streak (observability), then stores the boundary.
    onWordBoundary: handleWordBoundary,
  });

  const stateMachine = useVoiceStateMachine({
    onEnterIdle: () => setError(null),
    onError: (reason) => { if (reason === 'timeout') setError('Request timed out — try again'); },
  });
  const { state, toIdle, toRecording, toProcessing, toSpeaking, toError } = stateMachine;
  toErrorRef.current = toError;

  // TICKET-221: one barge-in per speaking streak. A latch (not text-dedupe):
  // stateRef only updates on re-render, so two different partials in the same
  // tick would otherwise both fire bargeIn (double startRecording). Cleared
  // when a new speaking streak starts (effect below).
  const sttBargeLatchedRef = useRef(false);
  // TICKET-221: refs, not deps — bargeIn is declared further down (a dep here
  // is a TDZ crash at render), and the recording layer captures mergePartial
  // once per stream, so a closed-over `state` would go stale mid-session.
  const bargeInRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    if (state === 'speaking') sttBargeLatchedRef.current = false;
  }, [state]);

  const mergePartial = useCallback((text, chunkIndex) => {
    if (!text) return;
    partialsRef.current[chunkIndex] = text;
    setTranscript(Object.keys(partialsRef.current)
      .map(Number).sort((a, b) => a - b)
      .map(i => partialsRef.current[i]).filter(Boolean).join(' '));

    // TICKET-221 — Streaming STT partial (chunkIndex -1) during speaking
    // is a barge-in signal: the user is speaking (captured by streaming-STT).
    // Guard against echo: only trigger if the partial is new and has real content.
    // Minimum 4 chars avoids single-word false positives and aligns with the
    // VAD's "speech start" sensitivity (see useVoiceBargeIn tuning).
    // NOT during awaiting_confirmation (AC-8: proposal TTS must finish first).
    if (chunkIndex === -1 && stateRef.current === 'speaking' && text.length >= 4
      && !sttBargeLatchedRef.current) {
      sttBargeLatchedRef.current = true;
      logVoiceEvent('barge_in', 'ok', 'User voice detected via STT partial — interrupting', {
        trigger: 'stt_partial',
        partial_length: text.length,
      });
      bargeInRef.current?.();
    }
  }, []);

  const cleanupSession = useCallback((reason) => {
    try { stopTTS(); } catch (err) { logVoiceEvent('tts_stop', 'error', err?.message || 'stop_tts_failed', { reason: reason || 'cleanup' }); }
    // 2026-04-20 (session 4, P0-A0 fix) — call the exported stopRecording
    // FIRST so its finalize/observability side effects fire, then forceCleanup
    // sweeps any residual state (stale mediaRecorder, timers, streamRef).
    // Prior code only called forceCleanup, bypassing stopRecording's exported
    // contract — which mic_stop tests (tests/voice/cleanup-stops-recording.test.js
    // + tests/voice/voice-flow.integration.test.jsx) require to be called.
    try { stopWhisperRef.current?.(); } catch (err) { logVoiceEvent('mic_stop', 'error', err?.message || 'stop_recording_failed', { reason: reason || 'cleanup' }); }
    try { forceCleanupRef.current?.(reason || 'cleanup'); } catch (err) { logVoiceEvent('mic_force_cleanup', 'error', err?.message || 'force_cleanup_failed', { reason: reason || 'cleanup' }); }
    if (abortControllerRef.current) { try { abortControllerRef.current.abort(); } catch { /* noop */ } abortControllerRef.current = null; }
    if (sentenceBufRef.current) { try { sentenceBufRef.current.reset(); } catch { /* noop */ } sentenceBufRef.current = null; }
    streamDoneRef.current = false;
    partialsRef.current = {};
    // 2026-06-06 — flush any open turn_latency window so a turn that ended
    // without audio (hang-up, error) still records its partial timings.
    try { turnLatencyRef.current?.flush(turnFlushMetaRef.current || {}); } catch { /* no-op */ }
    turnLatencyRef.current = null;
    turnFlushMetaRef.current = null;
    toIdle(reason || 'cleanup');
    logVoiceEvent('voice_session_cleanup', 'ok', reason || 'cleanup', { reason });
  }, [stopTTS, toIdle]);

  // 2026-04-20 Phase 2 (audit P1-3): this effect now only handles the
  // cooldown → toIdle transition AFTER TTS drains. Text persistence moved
  // to the post-SSE-loop path in sendToAgent so a TTS failure no longer
  // prevents the assistant text from being written to chat_messages —
  // text lives independently of audio.
  useEffect(() => {
    if (state === 'speaking' && !isSpeaking && !isPending && streamDoneRef.current) {
      streamDoneRef.current = false;
      // 2026-06-06 — TTS has drained: first_audio has fired, so the
      // end-to-end turn_latency window is complete. Flush once (idempotent).
      try { turnLatencyRef.current?.flush(turnFlushMetaRef.current || {}); } catch { /* no-op */ }
      turnLatencyRef.current = null;
      turnFlushMetaRef.current = null;
      const cooldown = setTimeout(() => toIdle('tts_drained'), 500);
      return () => clearTimeout(cooldown);
    }
    return undefined;
  }, [isSpeaking, isPending, state, toIdle]);

  const sendToAgent = useCallback(async (userText) => {
    if (!userText?.trim()) { toIdle('empty_transcript'); return; }
    const agentTimer = startTimer('agent_send');
    toProcessing('user_turn');
    aiTextRef.current = ''; aiFullTextRef.current = ''; setLastAiFullText('');
    toolCallsRef.current = []; setLastToolCalls([]);
    streamDoneRef.current = false;

    // Remember the last user transcript so a Retry can replay it without
    // re-recording the audio (CEO decision: transcripts are already clean
    // post-canonical-pass, replaying them is cheaper than re-transcribing).
    lastUserTurnRef.current = userText;

    // 2026-04-20 — sessionClosedRef gate. Suppresses late sentence buffer
    // flushes that arrive after endSession. Without it, the SSE reader
    // keeps buffering tokens, sentence buffer keeps firing onSentence,
    // and TTS plays the reply AFTER the overlay has closed — the
    // "completely out of sync, TTS late and transcript long and out of
    // context" report. Same gate applied in appendMessage so the ghost
    // turn doesn't pollute chat_messages either.
    const buf = createSentenceBuffer({
      // PHASE 2B (first-audio latency) — let the FIRST chunk of the reply cut on
      // an early clause boundary (comma/semicolon/colon past a ~25-char floor)
      // so the agent starts speaking on the first clause instead of waiting for
      // the first full sentence. Subsequent chunks revert to full-sentence
      // granularity (prosody + no extra per-clause TTS cost). seq-ordered TTS
      // playback (useStreamingTTS) is unaffected — only the cut point changes.
      firstChunkClause: true,
      onSentence: (s) => {
        if (sessionClosedRef.current) return;
        // TICKET-463 — set BEFORE speak() so the flag is true even if speak()
        // throws synchronously. This is the single place a turn requests TTS.
        ttsRequestedRef.current = true;
        speak(s);
        toSpeaking('first_sentence');
      },
    });
    sentenceBufRef.current = buf;

    // 2026-04-20 Phase 1 (audit remediation adjacent fix): hoist
    // clearHardTimeout + controller above the try so the `finally` block
    // can reference them even when the try throws before they'd otherwise
    // be declared. Previously a pre-auth failure (getAuthHeaders rejecting)
    // triggered a ReferenceError inside the finally, producing "Unhandled
    // Rejection: clearHardTimeout is not defined" rather than the real
    // error.
    let controller = null;
    let clearHardTimeout = () => {};
    try {
      const auth = await getAuthHeaders();
      controller = new AbortController();
      abortControllerRef.current = controller;

      // 2026-04-20 Phase 2 — hard 2-minute ceiling on the agent round-trip.
      // ChatGPT-style heartbeat fillers (768cdd2) keep the user engaged
      // during normal 5-15s waits; this guard trips only on genuine hangs
      // (API loops, dead provider, malformed SSE). Exceeds → abort, surface
      // a visible error, let the caller offer Retry.
      const hardTimeoutId = setTimeout(() => {
        try { controller.abort(new Error('thinking_timeout')); } catch { /* noop */ }
      }, copilotRef.current ? 280_000 : 120_000); // copilot turns run up to TURN_BUDGET_MS (240 s)
      clearHardTimeout = () => clearTimeout(hardTimeoutId);
      const basePacket = buildVoicePacket({
        app, contextProjectId, contextDealId,
        conversationHistory: conversationHistory || turnTranscriptRef.current,
      });
      // Ensure a stable conversation-level trace is set (first turn mints it;
      // subsequent turns reuse it for conversation-scoped queries).
      if (!conversationTraceIdRef.current) {
        conversationTraceIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `conv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
      // TICKET-CODEX-3368081299 — mint a FRESH trace for each turn so every
      // turn_latency row gets a unique trace_id. The conversation trace lives in
      // conversationTraceIdRef and is emitted as a separate `conversation_id`
      // field on each spine row for cross-turn grouping.
      const turnTraceId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `turn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      // 2026-06-06 (Conversation Telemetry Baseline) — open the per-turn
      // end-to-end latency window keyed by THIS turn's trace_id so the
      // stt / packet_built / agent / tts rows all join under one trace.
      // speech_end = now (the user finished speaking before this send).
      // VFL-0.1 — read stt_model at turn start so every flush path (normal and
      // cleanup) carries it. getStreamingSttModel() reads localStorage.acn_stt_model
      // with a guarded fallback to STREAMING_STT_DEFAULT ('gpt-realtime-whisper').
      // TICKET-CODEX-3385964279: use the shared helper so the reported model matches
      // the actual model used on every path (UVTP, streaming, STT_MODEL env override).
      const sttModelForTurn = getStreamingSttModel();
      const turnLatency = createTurnLatencyTracker(turnTraceId, {
        traceId: turnTraceId, userId: userIdRef.current, sttModel: sttModelForTurn });
      turnLatencyRef.current = turnLatency;
      // TICKET-463 — reset alongside the tracker it guards, so the two cannot
      // drift apart. Without this reset the flag would stay true for the rest
      // of the session after the first spoken turn, permanently suppressing
      // turn_complete: worse than the bug it fixes.
      ttsRequestedRef.current = false;
      turnLatency.markSpeechEnd();
      const agentStartedAt = Date.now();

      const resp = await fetch(API_AI, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Trace-Id': turnTraceId,
          ...auth,
        },
        signal: controller.signal,
        body: JSON.stringify(copilotRef.current ? {
          action: 'agent.chat', scope: 'quote_copilot', stream: true, message: userText,
          deal_id: copilotRef.current.dealId, quote_id: copilotRef.current.quoteId || undefined,
          trace_id: turnTraceId,
        } : {
          action: 'voice.chat', message: userText,
          trace_id: turnTraceId,
          session_id: sessionIdRef.current || undefined,
          project_id: contextProjectId || undefined,
          deal_id: contextDealId || undefined,
          view: activeView || null,
          conversation_history: conversationHistory || turnTranscriptRef.current.map(t => ({ role: t.role, content: t.text })),
          knowledge: basePacket,
          ...(pendingProposal?.proposal_id ? { confirmation_context: { active: true, proposal_id: pendingProposal.proposal_id } } : {}),
        }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || `Agent error (${resp.status})`);
      }

      const sseCtx = {
        sessionIdRef, setPacketUsed,
        aiTextRef, setLastAiText, aiFullTextRef, setLastAiFullText,
        toolCallsRef, setLastToolCalls,
        sentenceBuffer: buf, streamDoneRef,
        onToolProposal: (p) => { enqueueProposal(p); stateMachine.toAwaitingConfirmation('tool_proposal'); },
        // TICKET-098 — zero-tap learning: learn_correction executes inline
        // (no pill), so refresh the live correction map when its result
        // arrives. The trusted entity-verified write just landed in
        // domain_vocabulary; the refetch makes it effective on the very next
        // utterance without any tap.
        onToolResult: ({ name, ok }) => {
          if (name === 'learn_correction' && ok) refreshCorrectionMap();
        },
        onConfirmationResolved: ({ proposal_id, outcome }) => {
          dequeueProposal(proposal_id);
          // T2.3 — write resolution to voice_proposals for audit.
          resolveVoiceProposal({ supabase, proposalId: proposal_id, resolution: outcome });
          // Only return to idle when the queue fully drains; otherwise stay
          // in awaiting_confirmation so the next pill keeps focus.
          setPendingProposals((rest) => {
            const remaining = rest.filter((p) => p.proposal_id !== proposal_id);
            if (remaining.length === 0) toIdle(`confirmation_${outcome}_${proposal_id}`);
            return remaining;
          });
        },
      };

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          for (const event of parseSSE(part + '\n\n', {
            onParseError: (err, raw, meta) => {
              logVoiceEvent('sse_parse', 'error', err?.message || 'malformed sse data', {
                event_type: meta?.eventType,
                raw_preview: raw,
                source: 'voice.chat',
                trace_id: turnTraceId,
                user_id: userIdRef.current,
              });
            },
          })) {
            // First streamed token closes the agent segment of turn_latency.
            if (event?.type === 'text' && event?.data?.text) turnLatency.markFirstToken();
            if (copilotRef.current) {
              if (event?.type === 'done') turnLatency.markFirstToken();
              handleCopilotVoiceEvent(event, sseCtx);
            } else {
              handleVoiceSseEvent(event, sseCtx);
            }
          }
        }
      }

      if (!streamDoneRef.current) { buf.flush(); streamDoneRef.current = true; }
      setLastAiText(aiTextRef.current);
      agentTimer.ok(`Response: ${aiTextRef.current.length} chars`, { response_length: aiTextRef.current.length });
      // 2026-06-06 — stash the pre-window + diagnostic timings for this turn so
      // the turn_latency flush (fired when TTS drains, below) can report:
      //   stt_ms             the client STT-accept cost (measured before the
      //                      turn trace_id existed) — a pre-window sub-metric.
      //   agent_roundtrip_ms the full voice.chat fetch (speech_end -> stream
      //                      done). DIAGNOSTIC only — it overlaps tts_ms, so it
      //                      is reported separately and never overrides the
      //                      mark-derived agent_ms (speech_end -> first_token).
      // agent_ms / tts_ms stay mark-derived in turnLatency.js so they sum to
      // total_ms without double-counting synthesis.
      turnFlushMetaRef.current = {
        stt_ms: sttDurationMsRef.current,
        agent_roundtrip_ms: Date.now() - agentStartedAt,
      };
      // TICKET-912 slice 3: the copilot wrote its own turn (and maybe the quote) —
      // let the host re-read the thread / items and reload the quote.
      if (copilotRef.current && !sessionClosedRef.current) {
        try { copilotRef.current.onTurnDone?.(); } catch (e) { logVoiceEvent('copilot_turn_done_cb', 'error', e?.message || 'callback_failed'); }
      }

      // 2026-04-20 Phase 2 (audit P1-3): persist the assistant turn NOW,
      // as soon as the stream is done — NOT when TTS drains. Previously
      // a TTS failure (fetch error, audio decode fail, user muted before
      // the reply) left the text stream delivered successfully but the
      // row never written to chat_messages because the useEffect gated on
      // isSpeaking/isPending transitioning false. Decoupling text from
      // audio means lost audio ≠ lost data. Still gated on sessionClosedRef
      // so a late `done` after endSession can't ghost-write.
      if (!sessionClosedRef.current && aiTextRef.current) {
        turnTranscriptRef.current.push({
          role: 'assistant',
          text: aiTextRef.current,
          ts: new Date().toISOString(),
        });
        updateVoiceSessionTurn({
          supabase,
          sessionId: sessionIdRef.current,
          transcript: turnTranscriptRef.current,
        }).catch((err) => logVoiceEvent('voice_session_turn_update', 'error',
          err?.message || 'update_failed',
          { session_id: sessionIdRef.current }));

        const finalAssistantText = aiFullTextRef.current || aiTextRef.current;
        const cid = chatConversationIdRef.current;
        if (cid && finalAssistantText) {
          appendMessage({
            conversationId: cid,
            role: 'assistant',
            content: finalAssistantText,
            source: 'voice',
          })
            .then(() => notifyChatMessagesAppended(cid))
            .catch((err) => logVoiceEvent('chat_persist_assistant', 'error',
              err?.message || 'persist_failed',
              { conversation_id: cid }));
        }
      }
      // T2.1+T2.2 — when the agent emits tool_proposal events, the SSE handler
      // already transitioned us to awaiting_confirmation. Don't clobber that
      // back to idle here; the queue must drain first (resolveProposal handles
      // the eventual idle transition). Read from the ref because the React
      // state setter from enqueueProposal hasn't flushed by the time this
      // line runs (same-microtask SSE event handling).
      // TICKET-463 — ttsRequestedRef is checked FIRST and is the authoritative
      // signal. isSpeaking/isPending are React state that has not flushed yet
      // at this point in the SSE loop, so on 2026-07-27 this branch fired at
      // 19:15:02.226 while TTS was already in flight (speak() at 19:15:01.263)
      // and audio began at 19:15:03.568. That sent the UI to idle 1.3 s before
      // the agent was audible AND orphaned the turn-latency tracker, which is
      // why turn_latency had 0 rows against 241 recorded turns.
      if (!ttsRequestedRef.current && !isSpeaking && !isPending && pendingProposalsRef.current.length === 0) {
        logVoiceEvent('turn_complete', 'ok', 'Turn complete (no TTS needed)');
        toIdle('turn_complete_no_tts');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // Distinguish hard timeout from user-initiated abort (barge-in,
        // endSession). Hard timeout → surface a visible error with a
        // recoverable state so Retry works; user abort → silent idle.
        const timedOut = String(err?.message || controller?.signal?.reason?.message || '').includes('thinking_timeout');
        if (timedOut) {
          const msg = 'Voice reply timed out after 2 minutes. Tap to retry.';
          setError(msg);
          toError('thinking_timeout');
          logVoiceEvent('agent_timeout', 'error', msg, { user_text_preview: (userText || '').slice(0, 120) });
        }
        return;
      }
      agentTimer.fail(err.message);
      console.error('[useVoiceConversation] sendToAgent error:', err.message);
      setError(err.message);
      toError(err.message);
    } finally {
      clearHardTimeout();
      abortControllerRef.current = null;
    }
  }, [app, contextProjectId, contextDealId, conversationHistory, activeView, speak, isSpeaking, isPending, pendingProposal, enqueueProposal, dequeueProposal, stateMachine, toIdle, toProcessing, toSpeaking, toError]);

  // Retry the last user turn without re-recording. Uses the cached
  // transcript (which is already post-canonical-pass) + the existing
  // session so the agent resumes context. Surfaces any subsequent errors
  // the same way as the original send.
  const retryLastTurn = useCallback(() => {
    const text = lastUserTurnRef.current;
    if (!text) return;
    setError(null);
    sendToAgent(text);
  }, [sendToAgent]);

  const handleTranscript = useCallback((text) => {
    // 2026-06-06 (Conversation Telemetry Baseline) — time the STT accept stage
    // so the 'stt' row carries duration_ms (the empirical baseline showed stt
    // rows WITHOUT it). This is the accept/guard cost on the client; the STT
    // transcribe latency itself is on uvtp_finalize. user_id is the real auth
    // uid for CARL completeness.
    const sttTimer = startTimer('stt', { user_id: userIdRef.current });
    const sttStartedAt = Date.now();
    // Phase 1 (audit P0-3): drop transcripts that arrive after hangup.
    // Primary defense is cancelPendingTranscript() in useWhisperRecording;
    // this guard catches any race where a response slips through.
    if (sessionClosedRef.current) {
      sttTimer.fail('Transcript arrived after session closed — dropping', {
        text_length: text?.length || 0,
        text_preview: (text || '').slice(0, 80),
      });
      return;
    }
    partialsRef.current = {};
    if (!text?.trim()) { sttTimer.fail('STT returned empty text'); toIdle('empty_transcript'); return; }

    // Whisper emits training-data artifacts ("Thank you for watching",
    // YouTube-caption boilerplate) on near-silent audio. Reject before the
    // hallucinated text lands as a real user turn and triggers a pointless
    // agent round-trip — which is what produced the duplicate user bubble
    // + duplicate assistant reply in the 2026-04-20 voice screenshot.
    //
    // 90-07 AC-2 — also pass vocab terms so vocab_echo rule catches
    // Whisper reflecting the vocab_hints prompt back as transcript on
    // low-energy audio (e.g. "SY Artemis — Full Refit, Muluc, Private,").
    // Vocab terms: project names + deal names from AppData (same set
    // the server uses to build vocab_hints).
    const vocabTerms = [
      ...(app?.projects || []).map((p) => p?.project_name).filter(Boolean),
      ...(app?.crm || []).map((d) => d?.deal_name).filter(Boolean),
      ...(app?.vessels || []).map((v) => v?.vessel_name).filter(Boolean),
    ].map((s) => String(s).toLowerCase());
    const { hallucination, reason } = detectSttHallucination(text, { vocabTerms });
    if (hallucination) {
      sttTimer.fail(`STT rejected as hallucination: ${reason}`, {
        reason,
        text_length: text.length,
        text_preview: text.slice(0, 120),
      });
      toIdle('stt_hallucination');
      return;
    }

    // 2026-06-07 — vocab-on-final: apply the cached ACN dictionary alias map to
    // the (possibly raw, streaming) transcript so the displayed turn + the agent
    // input + persistence all carry the canonical term. Idempotent for the batch
    // path (already server-corrected -> no replacements). Best-effort: raw text
    // proceeds if normalization throws.
    let finalText = text;
    try {
      const { normalized, replacements } = normalizeTranscriptSync(text, correctionMapRef.current);
      if (replacements && replacements.length) {
        finalText = normalized;
        logVoiceEvent('stt_vocab_correct', 'ok', `applied ${replacements.length} vocab correction(s)`, {
          corrections: replacements.map((r) => `${r.from}->${r.to}`).join(', '),
          user_id: userIdRef.current,
        });
      }
    } catch {
      // spine-emit-ok: best-effort vocab normalize; finalText stays raw
    }

    // TICKET-076 — LIVE entity-name correction. The exact domain_vocabulary pass
    // above only catches names a human curated into misheard_as; a brand-new
    // project (CEO "Yaiza" report 2026-06-10) is invisible to it AND to the box
    // matcher (which primes from domain_vocabulary too). Run the conservative
    // proper-noun phonetic matcher against the user's LIVE project/vessel/deal
    // names — the same set already gathered for hallucination detection below —
    // so a close mishearing snaps to the canonical name on the DISPLAYED turn +
    // agent input + persistence. Same precision guards as the box matcher
    // (services/stt-proxy/vocabCorrect.js): phonetic-key equal + Levenshtein<=2 +
    // COMMON_WORDS guard. Best-effort — raw text proceeds if it throws.
    try {
      const entityNames = [
        ...(app?.projects || []).map((p) => p?.project_name),
        ...(app?.crm || []).map((d) => d?.deal_name),
        ...(app?.vessels || []).map((v) => v?.vessel_name),
      ].filter(Boolean);
      const { text: nameCorrected, corrections } = correctProperNounsSync(finalText, buildNameIndex(entityNames));
      if (corrections.length) {
        finalText = nameCorrected;
        logVoiceEvent('stt_vocab_correct', 'ok', `applied ${corrections.length} proper-noun correction(s)`, {
          corrections: corrections.join(', '),
          source: 'live_entity_names',
          user_id: userIdRef.current,
        });
      }
    } catch {
      // spine-emit-ok: best-effort proper-noun correction; finalText stays as-is
    }

    sttTimer.ok(`Transcribed: "${finalText.slice(0, 80)}"`, { text_length: finalText.length });
    // Stash the STT accept duration so sendToAgent's turn_latency flush can
    // report stt_ms even though STT happened before the turn trace_id minted.
    sttDurationMsRef.current = Date.now() - sttStartedAt;
    setTranscript(finalText);
    turnTranscriptRef.current.push({ role: 'user', text: finalText, ts: new Date().toISOString() });

    // B4: persist user voice turn to chat_messages(source='voice') so it
    // survives reload, shows in sidebar, and joins text turns in one ordered
    // timeline. Fire-and-forget — voice UX must not block on DB.
    // 2026-06-06 (Conversation Telemetry Baseline) - time the persist stage
    // so voice_conversation_persisted carries duration_ms (was untimed).
    const persistStartedAt = Date.now();
    // TICKET-912 slice 3: the Quote Copilot keeps its own per-deal thread — voice turns
    // must not also land in the general chat_messages timeline.
    if (!copilotRef.current) ensureChatConversationForVoice({
      supabase,
      voiceSessionId: sessionIdRef.current,
      existingChatId: chatConversationIdRef.current,
      contextProjectId,
      contextDealId,
    })
      .then((cid) => {
        if (!cid) return;
        const wasUnbound = !chatConversationIdRef.current;
        chatConversationIdRef.current = cid;
        // 2026-04-21 persistence fix — if we just created a new
        // conversation (AgentShell opened voice with currentId=null),
        // emit the cid back to the parent so its chat view is pointed
        // at the new conversation. Without this, closing the overlay
        // leaves the voice turns in an orphan "Voice session" row that
        // AgentShell's chat never subscribes to — user sees "wiped".
        if (wasUnbound && typeof onConversationPersistedRef.current === 'function') {
          try { onConversationPersistedRef.current(cid); } catch { /* noop */ }
          logVoiceEvent('voice_conversation_persisted', 'ok',
            'New chat conversation created for voice session — propagated to parent',
            { conversation_id: cid, duration_ms: Date.now() - persistStartedAt, user_id: userIdRef.current });
          // 2026-04-21 (Trace 4) — seed the conversation title from the
          // first user utterance instead of leaving the hardcoded
          // "Voice session" placeholder. CEO report 2026-04-21 22:38:
          // sidebar was full of "Voice session" entries that all looked
          // identical — impossible to tell which thread was which.
          // Only renames if the current title is still the placeholder
          // so a user's manual rename is never overwritten. Fire-and-
          // forget: failure doesn't block transcript persistence.
          const seedTitle = finalText.trim().slice(0, 60);
          if (seedTitle) {
            supabase
              .from('chat_conversations')
              .update({ title: seedTitle })
              .eq('id', cid)
              .eq('title', 'Voice session')
              .then(({ error: titleErr }) => {
                if (titleErr) console.warn('[voice] title seed failed:', titleErr.message);
              });
          }
        }
        return appendMessage({ conversationId: cid, role: 'user', content: finalText, source: 'voice' })
          .then(() => notifyChatMessagesAppended(cid));
      })
      .catch((err) => logVoiceEvent('chat_persist_user', 'error',
        err?.message || 'persist_failed',
        { conversation_id: chatConversationIdRef.current, user_id: userIdRef.current }));

    sendToAgent(finalText);
  }, [sendToAgent, toIdle, contextProjectId, contextDealId]);

  const handleRecordError = useCallback((msg) => {
    logVoiceEvent('mic_error', 'error', msg);
    console.error('[useVoiceConversation] recording error:', msg);
    setError(msg);
    toError(msg);
  }, [toError]);

  const { recording, transcribing, startRecording, stopRecording: stopWhisper, muteMic: whisperMute, unmuteMic: whisperUnmute, cancelPendingTranscript, forceCleanup: whisperForceCleanup } = useWhisperRecording({
    onTranscript: handleTranscript,
    onError: handleRecordError,
    contextType: contextProjectId ? 'project' : contextDealId ? 'deal' : activeView || 'general',
    contextId: contextProjectId || contextDealId || undefined,
    contextHint: 'voice_conversation',
    mode: 'conversation',
    onPartial: mergePartial,
    language,
  });
  stopWhisperRef.current = stopWhisper;
  // Phase 1 (audit P0-3 ghost-send): endSession must cancel any in-flight
  // finalize so the detached IIFE in useWhisperRecording doesn't call
  // onTranscript (→ handleTranscript → sendToAgent) after the user hangs up.
  const cancelPendingTranscriptRef = useRef(() => {});
  cancelPendingTranscriptRef.current = cancelPendingTranscript;
  // 2026-04-20 (session 3) — forceCleanup bridge for endSession. Must survive
  // ref-capture because endSession's closure may be stale by the time it fires.
  const forceCleanupRef = useRef(() => {});
  forceCleanupRef.current = whisperForceCleanup;

  // 2026-04-20 (session 4, P0-A1 fix) — mic/TTS control catches now log via
  // logVoiceEvent. Mic-permission revoke, dead MediaStream, and TTS pause
  // failures were silent — UI state diverged from actual track state with no
  // app_error_logs row to debug from. The CLAUDE.md "no silent failures" rule
  // is zero-tolerance on the voice path.
  const muteMic = useCallback(() => {
    try { whisperMute?.(); }
    catch (err) { logVoiceEvent('mic_mute', 'error', err?.message || 'mute_failed'); }
  }, [whisperMute]);
  const unmuteMic = useCallback(() => {
    try { whisperUnmute?.(); }
    catch (err) { logVoiceEvent('mic_unmute', 'error', err?.message || 'unmute_failed'); }
  }, [whisperUnmute]);
  const pauseSpeaking = useCallback(() => {
    try { pauseTTS?.(); }
    catch (err) { logVoiceEvent('tts_pause', 'error', err?.message || 'pause_failed'); }
    setVadPaused(true);
    logVoiceEvent('vad_pause', 'ok', 'user paused — VAD suspended');
  }, [pauseTTS]);
  const resumeSpeaking = useCallback(() => {
    try { resumeTTS?.(); }
    catch (err) { logVoiceEvent('tts_resume', 'error', err?.message || 'resume_failed'); }
    setVadPaused(false);
    logVoiceEvent('vad_resume', 'ok', 'user resumed — VAD re-enabled');
  }, [resumeTTS]);

  const startTurn = useCallback(() => {
    if (state !== 'idle') return;
    // Phase 1 (audit P0-3): clear the "session closed" flag so a re-opened
    // overlay accepts transcripts again. endSession sets this to true;
    // startTurn is the first user action that revives the session.
    sessionClosedRef.current = false;
    unlockAudio();
    // PHASE 2B (first-audio latency) — warm the auth/TLS path the moment the
    // turn starts, while the user is still speaking. getAuthHeaders() refreshes
    // the supabase session if near-expiry; doing it now means sendToAgent's
    // `await getAuthHeaders()` and the first speak()'s TTS fetch don't pay the
    // token-refresh + TLS warmup on the critical first-audio path. Fire-and-
    // forget; the real awaited call still runs in sendToAgent/speak.
    getAuthHeaders().catch((err) => logVoiceEvent('auth_prewarm', 'error', err?.message || 'prewarm_failed'));
    setError(null); setTranscript(''); setPacketUsed(null);
    toRecording('ptt_press');
    logVoiceEvent('mic_start', 'ok', 'PTT pressed — starting recording');
    startRecording();
    // 2026-04-20 (session 4, P0-A2 fix) — was `.catch(console.warn)`, which
    // kept the entire voice session running with sessionIdRef = null. Every
    // downstream log (mic_stop, tts_*, action_result) then has no session
    // correlation ID, making the session impossible to trace in SQL. Now logs
    // to app_error_logs so SQL can surface the failure frequency.
    ensureVoiceSession({ supabase, existingId: sessionIdRef.current, contextProjectId, contextDealId })
      .then(id => { if (id) sessionIdRef.current = id; })
      .catch(err => logVoiceEvent('voice_session_create', 'error', err?.message || 'ensure_session_failed', {
        context_project_id: contextProjectId || null,
        context_deal_id: contextDealId || null,
      }));
  }, [state, unlockAudio, startRecording, contextProjectId, contextDealId, toRecording]);

  const stopRecording = useCallback(() => {
    if (state !== 'recording') return;
    stopWhisper();
    toProcessing('ptt_release');
  }, [state, stopWhisper, toProcessing]);

  const stopSpeaking = useCallback(() => {
    stopTTS();
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (sentenceBufRef.current) { sentenceBufRef.current.reset(); sentenceBufRef.current = null; }
    streamDoneRef.current = false;
    toIdle('stop_speaking');
  }, [stopTTS, toIdle]);

  const endSession = useCallback(async () => {
    // 2026-04-20 — always stop TTS + whisper unconditionally. Previously
    // this only ran when state was 'recording' / 'speaking', but the
    // sentence buffer / TTS queue could still have pending audio after a
    // quick state transition — which is what caused "background makes
    // noises" when the user hit the back chevron mid-reply.
    //
    // Phase 1 (audit P0-3 ghost-send): mark the session closed FIRST so
    // handleTranscript drops any transcript that slips through, and call
    // cancelPendingTranscript to abort the in-flight finalize fetch and
    // invalidate the captured generation inside useWhisperRecording's IIFE.
    sessionClosedRef.current = true;
    try { cancelPendingTranscriptRef.current?.(); } catch (err) { logVoiceEvent('mic_cancel_pending', 'error', err?.message || 'cancel_pending_failed', { reason: 'end_session' }); }
    // 2026-04-20 (session 4, P0-A0 fix) — call exported stopRecording FIRST
    // so its finalize/observability contract fires (test contract), then
    // forceCleanup sweeps residual state. Prior code only called forceCleanup
    // and the 4 mic-orphan regression tests went red (missing stopRecordingSpy
    // call on visibility-hidden / interrupted / end-session paths).
    try { stopWhisperRef.current?.(); } catch (err) { logVoiceEvent('mic_stop', 'error', err?.message || 'stop_recording_failed', { reason: 'end_session' }); }
    try { forceCleanupRef.current?.('end_session'); } catch (err) { logVoiceEvent('mic_force_cleanup', 'error', err?.message || 'force_cleanup_failed', { reason: 'end_session' }); }
    try { stopTTS(); } catch (err) { logVoiceEvent('tts_stop', 'error', err?.message || 'stop_tts_failed', { reason: 'end_session' }); }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (sentenceBufRef.current) { sentenceBufRef.current.reset(); sentenceBufRef.current = null; }
    streamDoneRef.current = false;
    toIdle('end_session');
    await closeVoiceSession({ supabase, sessionId: sessionIdRef.current, transcript: turnTranscriptRef.current });
    sessionIdRef.current = null;
    // Phase 2 (audit P1-5): restore the seed from the prop so if the user
    // reopens the overlay against the same chat conversation, turns go to
    // that convo instead of spawning a new "Voice session" row.
    chatConversationIdRef.current = existingChatConversationId || null;
    turnTranscriptRef.current = [];
    // TICKET-1028: `state` was listed here but the body never branches on it
    // (it unconditionally stops whisper/TTS/cleanup every call) — every voice
    // state transition recreated this function for no reason. Dropped.
  }, [stopWhisper, stopTTS, toIdle]);

  const bargeIn = useCallback(() => {
    stopTTS();
    if (abortControllerRef.current) { try { abortControllerRef.current.abort(); } catch { /* noop */ } }
    if (sentenceBufRef.current) { sentenceBufRef.current.reset(); sentenceBufRef.current = null; }
    streamDoneRef.current = false;
    partialsRef.current = {};
    setTranscript('');
    toRecording('barge_in');
    startRecording();
  }, [stopTTS, startRecording, toRecording]);
  bargeInRef.current = bargeIn; // TICKET-221: mergePartial (declared above) fires it via ref

  // TICKET-097 — press-and-hold orb override (deterministic manual barge-in).
  // While held: TTS is hard-stopped, the in-flight stream aborted, and the
  // main auto-mode VAD suspended (vadPaused) so nothing auto-ends the turn —
  // a long message records until the finger lifts. Release stops + processes.
  const holdToTalkStart = useCallback(() => {
    setVadPaused(true);
    logVoiceEvent('hold_to_talk', 'ok', 'hold start — VAD suspended, recording until release', { state });
    if (state === 'recording') return; // already capturing — just suppress auto-end
    if (state === 'idle') { startTurn(); return; }
    bargeIn(); // speaking / processing → hard-stop TTS + abort + record
  }, [state, startTurn, bargeIn]);

  const holdToTalkEnd = useCallback(() => {
    setVadPaused(false);
    logVoiceEvent('hold_to_talk', 'ok', 'hold release — stopping + processing turn', { state });
    stopRecording();
  }, [state, stopRecording]);

  useVoiceAutoMode({
    enabled: voiceMode === 'auto' && !vadPaused,
    state,
    onSpeechStart: startTurn,
    onSpeechEnd: stopRecording,
    // TICKET-VOICE-CONVO-UVTP — surface getUserMedia rejections so CEO sees a
    // visible error instead of an idle orb forever. Was a silent console.warn
    // pre-fix; now logged to spine + shown to user.
    onInitError: (err, phase) => {
      if (err?.name === 'NotAllowedError') {
        setError('Microphone access required — tap to retry');
      } else if (err?.name === 'NotFoundError') {
        setError('No microphone found — check your headset');
      } else {
        setError(`Microphone init failed (${phase}) — tap to retry`);
      }
    },
  });
  // TICKET-103: wire isSourceActive from useStreamingTTS so the rolling noise
  // floor only adapts while an AudioBufferSource is genuinely playing.
  // isSourceActive is a stable function closing over currentSourceRef — no
  // memoization needed, and it doesn't cause useVoiceBargeIn to remount.
  useVoiceBargeIn({ state, onBargeIn: bargeIn, active: state === 'speaking', getSourceActive: isSourceActive, getReferenceEnvelope });

  useAudioSessionCleanup({
    state,
    audioContextRef,
    streamRef: vadStreamRef,
    onCleanup: cleanupSession,
    onStreamLost: () => { setError('microphone unavailable'); toError('stream_lost'); },
  });

  // T2.1+T2.2 — chief-of-staff queue: target a specific proposal_id, falling
  // back to the head of the queue if no id is supplied (backwards compat for
  // the singular ConfirmationPill in earlier UIs). The server emits a
  // confirmation_resolved SSE which dequeues + transitions state; this local
  // dequeue is a safety net so the pill disappears immediately on tap even if
  // the SSE round-trip is slow.
  const resolveProposal = useCallback(async (kind, proposalId) => {
    const target = proposalId
      ? pendingProposals.find((p) => p.proposal_id === proposalId)
      : pendingProposals[0];
    if (!target) return;
    try {
      const auth = await getAuthHeaders();
      // TICKET-104: always send proposal_id so the server can do DB fallback
      // when the in-memory registry is cold (different Vercel instance).
      const resp = await fetch(API_AI, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          action: `voice.${kind}`,
          session_id: sessionIdRef.current,
          proposal_id: target.proposal_id,
        }),
      });
      // TICKET-104: check resp.ok — previously this was fire-and-forget, so a 404
      // "No pending proposal" (cold-instance registry miss) looked like success,
      // the pill disappeared, and the tool never executed. Now surface the error.
      if (!resp.ok) {
        let serverMsg = `server returned ${resp.status}`;
        try { const body = await resp.json(); serverMsg = body?.error || serverMsg; } catch { /* spine-emit-ok: optional JSON parse of error body */ }
        logVoiceEvent('voice_proposal_tap', 'error', `${kind} failed: ${serverMsg}`, {
          proposal_id: target.proposal_id,
          kind,
          error: serverMsg,
          http_status: resp.status,
        });
        setError(`Could not ${kind} — ${serverMsg}`);
        // Do NOT dequeue — leave the pill so the user can retry or cancel.
        return;
      }
      logVoiceEvent('voice_proposal_tap', 'ok', `${kind}: ${target.name}`, {
        proposal_id: target.proposal_id,
        kind,
      });
      // TICKET-095 — make a confirmed correction effective on the very NEXT
      // utterance: optimistic insert into the live session map (so the next
      // transcript normalizes immediately) + background refetch (picks up the
      // canonical row the trusted server write just created, with entity ids).
      if (kind === 'confirm' && target.name === 'learn_correction') {
        insertCorrectionIntoMap(correctionMapRef.current, target.resolved_args || {});
        refreshCorrectionMap();
        // No raw spoken names in the payload (AEGIS spine-sanitizer rule) —
        // proposal_id is enough to join back to the proposal for debugging.
        logVoiceEvent('stt_vocab_learn_applied', 'ok', 'confirmed correction applied to live session map', {
          proposal_id: target.proposal_id,
        });
      }
    } catch (err) {
      // 2026-04-20 Phase 3 (audit P1-2): previously console.error-only. Now
      // both logs + surfaces to user via setError so a failed confirm/cancel
      // doesn't just silently leave the pill in place.
      console.error(`[useVoiceConversation] ${kind} error:`, err?.message);
      logVoiceEvent('voice_proposal_tap', 'error',
        `${kind} failed: ${err?.message || err}`, {
          proposal_id: target.proposal_id,
          kind,
          error: String(err?.message || err).slice(0, 200),
        });
      setError(`Could not ${kind} — ${err?.message || 'unknown error'}`);
      // TICKET-1027: this catch had no `return`, so execution fell through
      // into the unconditional dequeue + resolve below — a network error
      // silently dropped the pill and marked it resolved as if it had
      // succeeded. Bail out here; leave the proposal queued for retry.
      return;
    }
    // T2.3 — write resolution locally too. Server-side onConfirmationResolved
    // also writes; either path is idempotent because we set resolved_at on a
    // single row keyed by proposal_id.
    resolveVoiceProposal({ supabase, proposalId: target.proposal_id, resolution: kind });
    dequeueProposal(target.proposal_id);
    // Only return to idle when the queue fully drains; otherwise stay in
    // awaiting_confirmation so the next pill keeps focus.
    setPendingProposals((rest) => {
      const remaining = rest.filter((p) => p.proposal_id !== target.proposal_id);
      if (remaining.length === 0) toIdle(`tap_${kind}`);
      return remaining;
    });
  }, [pendingProposals, dequeueProposal, toIdle, refreshCorrectionMap]);

  const confirmProposal = useCallback((proposalId) => resolveProposal('confirm', proposalId), [resolveProposal]);
  const cancelProposal = useCallback((proposalId) => resolveProposal('cancel', proposalId), [resolveProposal]);

  return {
    state, recording, transcribing, isSpeaking,
    transcript, lastAiText, lastAiFullText, lastToolCalls,
    // TICKET-073 (karaoke) — per-word boundary signal for the reply highlight.
    karaoke,
    error, packetUsed, voiceMode,
    // Singular `pendingProposal` is the head of the queue (backwards compat
    // for any UI still rendering one pill); `pendingProposals` is the full
    // ordered array for the chief-of-staff multi-pill stack.
    pendingProposal, pendingProposals, confirmProposal, cancelProposal, clearProposals,
    startTurn, stopRecording, stopSpeaking, endSession, unlockAudio,
    muteMic, unmuteMic, pauseSpeaking, resumeSpeaking,
    // TICKET-097 — press-and-hold orb override.
    holdToTalkStart, holdToTalkEnd,
    // Phase 2 (voice-robustness plan) — replay the last user transcript
    // when a retry pill is tapped after a 2-minute thinking timeout.
    retryLastTurn,
  };
}
